import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import AppsIcon from '@material-ui/icons/Apps';
import CreateNewFolderOutlinedIcon from '@material-ui/icons/CreateNewFolderOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ExtensionOutlinedIcon from '@material-ui/icons/ExtensionOutlined';
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import SearchIcon from '@material-ui/icons/Search';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import TimelineOutlinedIcon from '@material-ui/icons/TimelineOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import FavoriteBorderOutlinedIcon from '@material-ui/icons/FavoriteBorderOutlined';
import { ComponentType } from 'react';
import { useLocation } from 'react-router-dom';
import { myralisTokens } from '../../myralis/tokens';
import { LogoFull } from './LogoFull';

const useStyles = makeStyles({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1400,
    display: 'flex',
    flexDirection: 'column',
    width: 244,
    padding: '18px 14px',
    color: myralisTokens.colors.white,
    background: myralisTokens.colors.sidebar,
    borderRight: '1px solid rgba(166, 179, 255, 0.18)',
    boxShadow: '18px 0 44px rgba(7, 26, 47, 0.16)',
    fontFamily: myralisTokens.typography.body,
    overflowY: 'auto',
  },
  brandLink: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 108,
    marginBottom: 12,
    padding: '4px 6px',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  navGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  separator: {
    height: 1,
    margin: '18px -14px',
    background: 'rgba(255, 255, 255, 0.10)',
  },
  groupLabel: {
    margin: '18px 10px 8px',
    color: 'rgba(255, 255, 255, 0.48)',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 42,
    padding: '0 12px',
    color: 'rgba(255, 255, 255, 0.78)',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '& svg': {
      width: 22,
      height: 22,
      color: 'rgba(255, 255, 255, 0.72)',
      flex: '0 0 auto',
    },
    '&:hover': {
      color: myralisTokens.colors.white,
      background: myralisTokens.colors.sidebarHover,
      textDecoration: 'none',
    },
  },
  navLinkActive: {
    color: myralisTokens.colors.white,
    background: myralisTokens.colors.sidebarActive,
    boxShadow: `inset 3px 0 0 ${myralisTokens.colors.green}`,
    '& svg': {
      color: myralisTokens.colors.green,
    },
  },
  plannedLink: {
    opacity: 0.48,
    cursor: 'default',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.78)',
      background: 'transparent',
    },
  },
  utility: {
    marginTop: 'auto',
    paddingTop: 16,
  },
  version: {
    margin: '14px 10px 0',
    color: 'rgba(255, 255, 255, 0.42)',
    fontSize: 11,
    fontWeight: 700,
  },
});

type MenuItemStatus = 'enabled' | 'planned' | 'hidden';

type MenuItemDefinition = {
  id: string;
  label: string;
  href: string;
  icon: ComponentType;
  status: MenuItemStatus;
  exact?: boolean;
  activePatterns: string[];
};

type MenuGroupDefinition = {
  id: string;
  label?: string;
  items: MenuItemDefinition[];
};

type MyralisTopNavProps = {
  navItems: any;
};

const menuGroups: MenuGroupDefinition[] = [
  {
    id: 'search',
    items: [
      {
        id: 'search',
        label: 'Busca',
        href: '/search',
        icon: SearchIcon,
        status: 'enabled',
        activePatterns: ['/search'],
      },
    ],
  },
  {
    id: 'main',
    items: [
      {
        id: 'home',
        label: 'Home',
        href: '/home',
        icon: HomeOutlinedIcon,
        status: 'enabled',
        exact: true,
        activePatterns: ['/home'],
      },
      {
        id: 'catalog',
        label: 'Catálogo',
        href: '/?filters[kind]=component&filters[user]=all&limit=20',
        icon: AppsIcon,
        status: 'enabled',
        activePatterns: [
          '/?filters[kind]=component&filters[user]=all&limit=20',
          '/?filters%5Bkind%5D=component&filters%5Buser%5D=all&limit=20',
        ],
      },
      {
        id: 'my-group',
        label: 'Meu Grupo',
        href: '/?filters[kind]=group&filters[user]=all&limit=20',
        icon: GroupOutlinedIcon,
        status: 'enabled',
        activePatterns: [
          '/?filters[kind]=group&filters[user]=all&limit=20',
          '/?filters%5Bkind%5D=group&filters%5Buser%5D=all&limit=20',
        ],
      },
      {
        id: 'api-docs',
        label: 'APIs',
        href: '/api-docs',
        icon: ExtensionOutlinedIcon,
        status: 'enabled',
        activePatterns: ['/api-docs'],
      },
      {
        id: 'docs',
        label: 'Documentação',
        href: '/docs',
        icon: DescriptionOutlinedIcon,
        status: 'enabled',
        activePatterns: ['/docs'],
      },
      {
        id: 'create',
        label: 'Criar...',
        href: '/create',
        icon: CreateNewFolderOutlinedIcon,
        status: 'enabled',
        activePatterns: ['/create'],
      },
      {
        id: 'catalog-import',
        label: 'Importar Repo',
        href: '/catalog-import',
        icon: AddCircleOutlineIcon,
        status: 'enabled',
        activePatterns: ['/catalog-import'],
      },
      {
        id: 'explore',
        label: 'Explorar',
        href: '/catalog-graph',
        icon: AppsIcon,
        status: 'enabled',
        activePatterns: ['/catalog-graph'],
      },
    ],
  },
  {
    id: 'tools',
    label: 'Ferramentas',
    items: [
      {
        id: 'system-health',
        label: 'System Health',
        href: '/system-health',
        icon: FavoriteBorderOutlinedIcon,
        status: 'enabled',
        activePatterns: ['/system-health'],
      },
      {
        id: 'tech-radar',
        label: 'Tech Radar',
        href: '/tech-radar',
        icon: TimelineOutlinedIcon,
        status: 'planned',
        activePatterns: ['/tech-radar'],
      },
      {
        id: 'visualizer',
        label: 'Visualizador',
        href: '/visualizer',
        icon: VisibilityOutlinedIcon,
        status: 'enabled',
        activePatterns: ['/visualizer'],
      },
    ],
  },
  {
    id: 'utility',
    label: 'Utilidades',
    items: [
      {
        id: 'help',
        label: 'Ajuda',
        href: '/docs/default/component/arquitetura/conteudo-migrado/recursos/onboarding/',
        icon: HelpOutlineIcon,
        status: 'planned',
        activePatterns: ['/docs/default/component/arquitetura/conteudo-migrado/recursos/onboarding'],
      },
      {
        id: 'settings',
        label: 'Configurações',
        href: '/settings',
        icon: SettingsOutlinedIcon,
        status: 'enabled',
        activePatterns: ['/settings'],
      },
    ],
  },
];

const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/';

const isActive = (pathname: string, search: string, item: MenuItemDefinition) => {
  const currentPath = normalizePath(pathname);
  const currentPathWithSearch = `${currentPath}${search}`;

  return item.activePatterns.some(pattern => {
    const normalizedPattern = normalizePath(pattern);

    if (normalizedPattern.includes('?')) {
      return (
        currentPathWithSearch === normalizedPattern ||
        decodeURIComponent(currentPathWithSearch) === normalizedPattern
      );
    }

    if (normalizedPattern === '/') {
      return currentPath === '/' && !search;
    }

    if (item.exact) {
      return currentPath === normalizedPattern && !search;
    }

    return (
      currentPath === normalizedPattern ||
      currentPath.startsWith(`${normalizedPattern}/`)
    );
  });
};

export const MyralisTopNav = (_props: MyralisTopNavProps) => {
  const classes = useStyles();
  const location = useLocation();

  const renderMenuItem = (item: MenuItemDefinition) => {
    if (item.status === 'hidden') {
      return null;
    }

    const Icon = item.icon;
    const active = item.status === 'enabled' && isActive(location.pathname, location.search, item);
    const className = [
      classes.navLink,
      active ? classes.navLinkActive : '',
      item.status === 'planned' ? classes.plannedLink : '',
    ]
      .filter(Boolean)
      .join(' ');

    if (item.status === 'planned') {
      return (
        <span key={item.id} className={className} aria-disabled="true">
          <Icon />
          {item.label}
        </span>
      );
    }

    return (
      <Link key={item.id} to={item.href} className={className}>
        <Icon />
        {item.label}
      </Link>
    );
  };

  return (
    <aside className={`${classes.root} myralis-side-nav`}>
      <Link to="/home" className={classes.brandLink} aria-label="Home">
        <LogoFull variant="sidebar" />
      </Link>

      {menuGroups.map((group, index) => (
        <div key={group.id}>
          {index === 1 && <div className={classes.separator} />}
          {group.label && <div className={classes.groupLabel}>{group.label}</div>}
          <nav className={classes.navGroup} aria-label={group.label ?? 'Navegação principal'}>
            {group.items.map(renderMenuItem)}
          </nav>
        </div>
      ))}

      <div className={classes.utility}>
        <div className={classes.version}>Myralis Dev Portal</div>
      </div>
    </aside>
  );
};
